import type { CaptureLogOptions, CaptureResult } from 'posthog-js'

const REDACTED = '[REDACTED]'

const SENSITIVE_KEYS = new Set([
  'token',
  'accesstoken',
  'refreshtoken',
  'authtoken',
  'authorization',
  'proxyauthorization',
  'password',
  'passwd',
  'cookie',
  'setcookie',
  'twofactorauthcode',
  'apikey',
  'secret',
  'credentials',
])

const SENSITIVE_TEXT_KEY =
  '(?:access[_-]?token|refresh[_-]?token|auth[_-]?token|token|authorization|proxy-authorization|password|passwd|cookie|set-cookie|twoFactorAuthCode|api[_-]?key|secret|credentials)'

const HEADER_VALUE = new RegExp(`(^|\\r?\\n)(${SENSITIVE_TEXT_KEY})\\s*:\\s*[^\\r\\n]*`, 'gi')
const ASSIGNED_VALUE = new RegExp(
  `((?:^|[?&;,\\s{])(?:["']?${SENSITIVE_TEXT_KEY}["']?)\\s*(?:=|:)\\s*)(?:"[^"]*"|'[^']*'|[^\\s&#,;}]+)`,
  'gi'
)
const BEARER_VALUE = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi

function normalizedKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEYS.has(normalizedKey(key))
}

/** Redact credentials when they appear inside URLs, headers or free-form error text. */
export function redactPosthogText(value: string): string {
  return value
    .replace(HEADER_VALUE, (_match, prefix: string, key: string) => `${prefix}${key}: ${REDACTED}`)
    .replace(ASSIGNED_VALUE, (_match, prefix: string) => `${prefix}${REDACTED}`)
    .replace(BEARER_VALUE, `Bearer ${REDACTED}`)
}

function scrubValue(value: unknown, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') return redactPosthogText(value)
  if (value === null || typeof value !== 'object' || value instanceof Date) return value
  if (seen.has(value)) return '[Circular]'

  seen.add(value)
  if (Array.isArray(value)) return value.map((item) => scrubValue(item, seen))

  const scrubbed: Record<string, unknown> = {}
  for (const [key, nestedValue] of Object.entries(value)) {
    scrubbed[key] = isSensitiveKey(key) ? REDACTED : scrubValue(nestedValue, seen)
  }
  return scrubbed
}

function scrubProperties(
  properties: Record<string, unknown>,
  preservePosthogProjectToken = false
): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {}
  const seen = new WeakSet<object>([properties])

  for (const [key, value] of Object.entries(properties)) {
    // PostHog requires its public project token in the event properties. A nested
    // `token` or one in person properties is still treated as sensitive.
    scrubbed[key] =
      preservePosthogProjectToken && key === 'token'
        ? value
        : isSensitiveKey(key)
          ? REDACTED
          : scrubValue(value, seen)
  }
  return scrubbed
}

/** Final privacy filter for analytics, autocapture and exception events. */
export function scrubPosthogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return null

  return {
    ...event,
    properties: scrubProperties(event.properties, true),
    ...(event.$set ? { $set: scrubProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: scrubProperties(event.$set_once) } : {}),
  }
}

/** Logs use a separate PostHog pipeline, so they need their own privacy filter. */
export function scrubPosthogLog(record: CaptureLogOptions): CaptureLogOptions {
  return {
    ...record,
    body: redactPosthogText(record.body),
    ...(record.attributes
      ? {
          attributes: scrubProperties(
            record.attributes as Record<string, unknown>
          ) as CaptureLogOptions['attributes'],
        }
      : {}),
  }
}
